/* eslint-disable tsdoc/syntax */
import { memo, useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Document, Page, pdfjs } from "react-pdf";
import Close from "@zenith/assets/icons/Close";
import Trash from "@zenith/assets/icons/Trash";

// Set workerSrc for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs";

export interface ImagePreviewComponentProps {
  showModal: boolean;
  title: string;
  onClickButton?: () => void;
  onClickClose: () => void;
  buttonText?: string;
  classNames?: string;
  image: string;
  showButtonIcon?: boolean;
}

const ImagePreviewComponent: React.NamedExoticComponent<ImagePreviewComponentProps> =
  memo(function ImagePreviewComponent(
    props: ImagePreviewComponentProps
  ): JSX.Element {
    const {
      image,
      onClickButton,
      onClickClose,
      showModal,
      buttonText,
      classNames,
      title,
      showButtonIcon,
    } = props;

    const [open, setOpen] = useState(showModal);
    const [imageUrl, setImageUrl] = useState("");
    const [numPages, setNumPages] = useState<number>(1);
    const [pageNumber, setPageNumber] = useState<number>(1);

    useEffect(() => {
      setOpen(showModal);
    }, [showModal]);

    useEffect(() => {
      setImageUrl(image);
      setPageNumber(1);
    }, [image]);

    const isPdf =
      title?.toLowerCase().endsWith(".pdf") ||
      !!imageUrl?.startsWith("data:application/pdf");

    return (
      <div>
        <Modal
          open={open}
          aria-labelledby="modal-modal-title"
          onClose={onClickClose}
          aria-describedby="modal-modal-description"
        >
          <Box className={`image-preview-modal ${classNames}`}>
            <div className="sticky top-0 z-10 flex h-auto w-full items-center justify-between bg-black pb-2">
              <span className="truncate pr-2 text-white">{title}</span>
              <IconButton onClick={onClickClose} aria-label="Close preview">
                <Close fill="white" />
              </IconButton>
            </div>
            <div className="flex w-full justify-center py-2">
              <div className="flex h-image-preview-size max-w-full items-center justify-center">
                {imageUrl && isPdf ? (
                  <div
                    className="flex h-full w-full flex-col items-center"
                    style={{ maxHeight: 1200 }}
                  >
                    {/* Scrollable PDF area */}
                    <div
                      style={{
                        overflow: "auto",
                        maxHeight: 800,
                        minHeight: 450,
                        width: "100%",
                      }}
                    >
                      <Document
                        file={imageUrl}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                        onLoadError={console.error}
                        loading={
                          <span className="text-white">Loading PDF...</span>
                        }
                        error={
                          <span className="text-red-500">
                            Failed to load PDF.
                          </span>
                        }
                      >
                        <Page
                          pageNumber={pageNumber}
                          width={500}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                    </div>
                    {/* Pagination controls */}
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
                        disabled={pageNumber <= 1}
                        className="rounded bg-gray-200 px-2 py-1 disabled:opacity-50"
                      >
                        Prev
                      </button>
                      <span className="text-white">
                        Page {pageNumber} of {numPages}
                      </span>
                      <button
                        onClick={() =>
                          setPageNumber((p) => Math.min(p + 1, numPages))
                        }
                        disabled={pageNumber >= numPages}
                        className="rounded bg-gray-200 px-2 py-1 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : (
                  imageUrl && (
                    <img
                      className="h-full w-auto max-w-full object-contain"
                      alt="preview"
                      src={imageUrl}
                      onError={(e) => {
                        console.error("Error loading image:", e);
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  )
                )}
              </div>
            </div>
            <div className={"flex grow items-end"}>
              <button
                id="delete-button"
                className="alternate-button gap-x-2"
                type="button"
                onClick={onClickButton}
              >
                {showButtonIcon ? (
                  <Trash
                    animate={true}
                    className="cursor-pointer"
                    fill="white"
                  />
                ) : (
                  ""
                )}
                {buttonText || ""}
              </button>
            </div>
          </Box>
        </Modal>
      </div>
    );
  });

export default ImagePreviewComponent;
